import React from "react";
import VariableArrayRow from "./VariableArrayRow";
import classnames from "classnames";
import { Button, Col, Input, InputGroup, Label, Row } from "reactstrap";
import ModelMediaContainer from "../Media/ModelMediaContainer";

/**
 * Content-side service building block (SCRUM-642) — image + description
 * pairs for the PAGES surfaces. A SEPARATE block from the settings
 * ServiceField (different placeholders, textarea size, fallback and
 * DB-unwrap semantics — do not merge them). Pinned sources:
 * contentVariables.types.test.jsx (wizard, SCRUM-630),
 * handleContentRequired.composites.test.jsx and
 * handleContentOptional.types.test.jsx (update, SCRUM-640), plus the e2e
 * wizard baseline variableBaselinePage.spec.ts.
 *
 * Emits `onChange(settingVar.variable, nextItems)` where nextItems is the
 * canonical page_content value: a NEW array of plain { image, description }
 * objects. The per-surface reporting envelopes stay host-side (SCRUM-643):
 * the wizard wraps it as onChangeVariableValue({ target: { name, value } })
 * (its handler mutates the Content entry and re-reports the whole Content
 * array via onContentChange); both update screens map it into Variables
 * immutably and call onVariableChange(updatedVariable). Service on the
 * Optional screen reports via onVariableChange, NOT onChangeContentOptional.
 *
 * variant="create" (wizard, both DnD buckets — byte-identical branches):
 * - image via the ModelMediaContainer picker ONLY (no URL input, no way to
 *   clear an image); picks without a truthy secure_url are ignored silently;
 * - labels "Image" / "Description" (no indices); the field-name header
 *   Label (Row.varible_container.mb-4) carries `summaryProps` — the optional
 *   bucket's drag-handle placement (the required bucket's separate always-
 *   visible handle div stays host chrome);
 * - container classes reuse the FAQ ones (faq-container / faq-item);
 * - description placeholder `insert value for ${variable}` with the BARE
 *   `value={item.description}` (no || "" fallback — wizard-identical);
 * - no add/remove buttons, no empty-image placeholder; preview img in
 *   Col md="7". Validation is host-owned (no inline feedback here).
 *
 * variant="update" (default; Required and Optional are character-identical):
 * - <details className="service-container mb-3" open> with the field name
 *   in the <summary> (summaryProps spread there); `key` is left to the
 *   parent; natively collapsible, no state;
 * - image is a plain URL text Input, placeholder "Paste image URL…"
 *   (U+2026), NO picker; "No image yet" span when empty; preview img in
 *   Col md="2";
 * - labels "Image #{i + 1}" / "Description #{i + 1}"; description
 *   placeholder "Type the description…" with the update-side || "" value
 *   fallbacks (image too);
 * - "+ Add service item" appends a blank pair; per-item &minus; button
 *   (danger, sm) disabled at length 1; removeItem itself stays unguarded.
 *
 * Preserved quirks (all surfaces): a NON-ARRAY value renders one blank
 * fallback pair render-only — the first edit materializes the length-1
 * array — while an EMPTY ARRAY renders zero rows (dead space on create,
 * addable on update); index-based React keys; input `name` attributes
 * `${variable}[${i}].image` / `.description` (e2e-relied).
 * Dropped (no DOM impact, per conventions): the wizard's dead
 * CLOUD_NAME/UPLOAD_PRESET consts. The single updateItem keeps the wizard's
 * string-or-event overload (the update surfaces only ever feed it events).
 */
export type ContentServiceItem = { image?: string; description?: string };

/** The update variant's image URL input and its preview, between the row label and the remove button. */
const ServiceImageCells = ({ item, i, fieldName, onChange }: {
  item: ContentServiceItem;
  i: number;
  fieldName: string | undefined;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}) => (
  <>
    <Col md="7">
      <Input
        type="text"
        placeholder="Paste image URL…"
        value={item.image || ""}
        onChange={onChange}
        name={`${fieldName}[${i}].image`}
      />
    </Col>
    <Col md="2" className="d-flex align-items-center">
      {item.image ? (
        <img
          src={item.image}
          alt={`service-${i}`}
          style={{ maxWidth: '100%', maxHeight: 60, objectFit: 'cover', borderRadius: 6 }}
        />
      ) : (
        <span style={{ fontSize: 12, color: "#888" }}>No image yet</span>
      )}
    </Col>
  </>
);

export type ContentServiceFieldProps = {
  settingVar: { variable?: string; value?: unknown };
  onChange: (name: string | undefined, value: ContentServiceItem[]) => void;
  variant?: "create" | "update";
  summaryProps?: Record<string, unknown>;
};

export default function ContentServiceField({
  settingVar,
  onChange,
  variant = "update",
  summaryProps = {},
}: ContentServiceFieldProps) {
  const fieldName = settingVar.variable;
  const items: ContentServiceItem[] = Array.isArray(settingVar.value)
    ? settingVar.value
    : [{ image: "", description: "" }];

  const setItems = (nextItems: ContentServiceItem[]) => onChange(fieldName, nextItems);

  const updateItem =
    (idx: number, field: keyof ContentServiceItem) =>
    (evtOrValue: string | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newVal =
      typeof evtOrValue === "string" ? evtOrValue : evtOrValue.target.value;
    setItems(
      items.map((item, i) => (i === idx ? { ...item, [field]: newVal } : item))
    );
  };

  const onMediaSelect = (idx: number) => (selectedImage: { secure_url?: string } | null | undefined) => {
    if (selectedImage && selectedImage.secure_url) {
      updateItem(idx, "image")(selectedImage.secure_url);
    }
  };

  const addItem = () => setItems([...items, { image: "", description: "" }]);
  const removeItem = (idx: number) => setItems(items.filter((_, i) => i !== idx));

  const descriptionArea = (i: number, text: string) => ({
    rows: 5,
    placeholder: "Type the description…",
    value: text,
    onChange: updateItem(i, "description"),
    name: `${fieldName}[${i}].description`,
  });

  if (variant === "create") {
    return (
      <div className="faq-container">
        <Row className="varible_container mb-4">
          <Label md="2" className="form-control-label" {...summaryProps}>
            {fieldName}
          </Label>
        </Row>

        {items.map((item, i) => (
          // index key: no add or remove in this variant, so rows never shift (SCRUM-2183)
          <div key={i} className="faq-item">
            <Row className="mb-3 varible_container">
              <Label md="2" className="form-control-label">
                Image
              </Label>

              <Col md="3">
                <ModelMediaContainer selectedImg={onMediaSelect(i)} />
              </Col>

              <Col md="7" className="d-flex align-items-center">
                {item.image && (
                  <img
                    src={item.image}
                    alt={`service-${i}`}
                    style={{ maxWidth: '100%', maxHeight: 60, objectFit: 'cover', borderRadius: 6 }}
                  />
                )}
              </Col>
            </Row>

            <Row className="varible_container">
              <Label md="2" className="form-control-label">
                Description
              </Label>
              <Col md="10">
                <InputGroup className={classnames("input-group-merge")}>
                  <textarea
                    rows={5}
                    cols={70}
                    placeholder={`insert value for ${settingVar.variable}`}
                    value={item.description}
                    onChange={updateItem(i, "description")}
                    name={`${fieldName}[${i}].description`}
                  />
                </InputGroup>
              </Col>
            </Row>
          </div>
        ))}
      </div>
    );
  }

  return (
    <details className="service-container mb-3" open>
      <summary
        className="varible_container form-control-label d-flex align-items-center"
        style={{ cursor: "pointer", userSelect: "none" }}
        {...summaryProps}
      >
        {fieldName}
      </summary>
      <div className="pt-3">
        {items.map((item, i) => (
          // index key: controlled row, no row-local state (SCRUM-2183)
          <VariableArrayRow key={i} className="service-item mb-3" topRowClassName="mb-3 varible_container"
            topLabel={`Image #${i + 1}`} onRemove={() => removeItem(i)} removeDisabled={items.length === 1}
            bottomRowClassName="varible_container" bottomLabel={`Description #${i + 1}`}
            textarea={descriptionArea(i, item.description || "")}>
            <ServiceImageCells item={item} i={i} fieldName={fieldName} onChange={updateItem(i, "image")} />
          </VariableArrayRow>
        ))}
        <Row className="mt-4">
          <Col md={{ offset: 2, size: 10 }}>
            <Button type="button" color="primary" onClick={addItem}>+ Add service item</Button>
          </Col>
        </Row>
      </div>
    </details>
  );
}

// renders its own <summary>/header chrome - the dispatcher skips its label row (SCRUM-643)
ContentServiceField.ownsChrome = true;
